import React, { useState, useEffect } from 'react';
import type { MetricWidgetConfig } from './MetricWidget';

interface MetricWidgetConfigProps {
  initialConfig: Partial<MetricWidgetConfig>;
  onChange: (config: MetricWidgetConfig) => void;
}

export function MetricWidgetConfigForm({ initialConfig = {}, onChange }: MetricWidgetConfigProps) {
  const [title, setTitle] = useState(initialConfig?.title || '');
  const [value, setValue] = useState(initialConfig?.value?.toString() || '');
  const [previousValue, setPreviousValue] = useState(initialConfig?.previousValue?.toString() || '');
  const [unit, setUnit] = useState(initialConfig?.unit || '$');
  const [icon, setIcon] = useState(initialConfig?.icon || 'DollarSign');
  const [color, setColor] = useState(initialConfig?.color || '#10b981');
  const [backgroundColor, setBackgroundColor] = useState(initialConfig?.backgroundColor || '');

  // Live update on every change
  useEffect(() => {
    const config = {
      title: title || 'Untitled',
      value: parseFloat(value) || 0,
      previousValue: previousValue ? parseFloat(previousValue) : undefined,
      unit,
      icon,
      color,
      backgroundColor: backgroundColor || undefined
    };
    onChange(config);
  }, [title, value, previousValue, unit, icon, color, backgroundColor]); // DON'T include onChange

  return (
    <div className="space-y-4 flex-1 overflow-auto">
      {/* Widget Background - Prominent */}
      <div className="bg-gray-50 dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-lg p-3">
        <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-3">
          Widget Background
        </label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={backgroundColor || '#ffffff'}
            onChange={(e) => setBackgroundColor(e.target.value)}
            className="w-14 h-14 rounded-lg cursor-pointer border-2 border-gray-400 dark:border-gray-500"
            title="Widget background color"
          />
          <div className="flex-1">
            <input
              type="text"
              value={backgroundColor}
              onChange={(e) => setBackgroundColor(e.target.value)}
              placeholder="#ffffff"
              className="w-full px-3 py-2 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white mb-1"
              maxLength={7}
            />
            {backgroundColor && (
              <button
                onClick={() => setBackgroundColor('')}
                className="text-xs text-red-600 dark:text-red-400 hover:underline"
                title="Clear background"
              >
                Reset to default
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Title */}
      <div>
        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
          Widget Title
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Revenue, Users, Sales"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
        />
      </div>

      {/* Value */}
      <div>
        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
          Value *
        </label>
        <input
          type="number"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="1200000"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
        />
      </div>

      {/* Previous Value (Optional) */}
      <div>
        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
          Previous Value (for comparison)
        </label>
        <input
          type="number"
          value={previousValue}
          onChange={(e) => setPreviousValue(e.target.value)}
          placeholder="1050000"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
        />
      </div>

      {/* Unit */}
      <div>
        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
          Unit
        </label>
        <select
          value={unit}
          onChange={(e) => setUnit(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
        >
          <option value="$">$ (Dollar)</option>
          <option value="%">% (Percent)</option>
          <option value="#"># (Number)</option>
          <option value="custom">Custom</option>
        </select>
      </div>

      {/* Icon Dropdown */}
      <div>
        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
          Icon
        </label>
        <select
          value={icon}
          onChange={(e) => setIcon(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
        >
          <option value="DollarSign">💰 Dollar Sign</option>
          <option value="TrendingUp">📈 Trending Up</option>
          <option value="TrendingDown">📉 Trending Down</option>
          <option value="Users">👥 Users</option>
          <option value="Building">🏢 Building</option>
          <option value="BarChart3">📊 Bar Chart</option>
          <option value="PieChart">🥧 Pie Chart</option>
          <option value="Zap">⚡ Zap</option>
          <option value="Target">🎯 Target</option>
          <option value="Activity">📈 Activity</option>
          <option value="Award">🏆 Award</option>
          <option value="Briefcase">💼 Briefcase</option>
          <option value="Calendar">📅 Calendar</option>
          <option value="Clock">🕐 Clock</option>
          <option value="CreditCard">💳 Credit Card</option>
          <option value="Heart">❤️ Heart</option>
          <option value="ShoppingCart">🛒 Cart</option>
          <option value="Star">⭐ Star</option>
        </select>
      </div>

      {/* Color Picker with Hex Input */}
      <div>
        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
          Color
        </label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="w-12 h-10 rounded cursor-pointer border border-gray-300 dark:border-gray-600"
          />
          <input
            type="text"
            value={color}
            onChange={(e) => {
              const hex = e.target.value;
              if (/^#[0-9A-F]{6}$/i.test(hex)) {
                setColor(hex);
              }
            }}
            placeholder="#10b981"
            className="flex-1 px-2 py-1 text-xs font-mono border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          />
        </div>
      </div>
    </div>
  );
}
